import { Fields, LetterTypes, Sources } from '../generator/types';

export type FieldPlaceholder = {
  key: Fields;
  placeholder: string;
};

export type ParagraphTemplate = {
  text: string;
  juvenileText?: string;
  fields: FieldPlaceholder[];
  contactAfter?: string;
  juvenileContactAfter?: string;
};

export type LetterBlock = {
  id: Sources;
  label: string;
  icon: string;
  paragraphs: ParagraphTemplate[];
  juvenileParagraphs?: ParagraphTemplate[];
};

export type LetterBlockSet = Partial<Record<Sources, LetterBlock>>;

export const REINSTATEMENT_DECLINE_BLOCKS: LetterBlockSet = {
  [Sources.medicalCondition]: {
    id: Sources.medicalCondition,
    label: 'Medical condition',
    icon: 'ti-stethoscope',
    paragraphs: [
      {
        text:
          'Our decision was based on medical history admitted on your ' +
          'reinstatement application for insurance. This information, based on ' +
          'our guidelines, disqualifies approval of your reinstatement request.',
        fields: [],
      },
    ],
  },
  [Sources.custom]: {
    id: Sources.custom,
    label: 'Custom',
    icon: 'ti-pencil',
    paragraphs: [
      {
        text: '{{customContent}}',
        fields: [
          {
            key: Fields.customContent,
            placeholder: 'Enter custom paragraph text',
          },
        ],
      },
    ],
  },
};

export const OTAF_SAME_BLOCKS: LetterBlockSet = {
  [Sources.appInfoDisclosure]: {
    id: Sources.appInfoDisclosure,
    label: 'App info / Disclosure',
    icon: 'ti-file-description',
    paragraphs: [
      {
        text:
          'We are letting you know your life insurance coverage has been ' +
          'approved. However, we are approving the policy without the ' +
          '{{riders}} due to {{riskGroup}} history admitted on your application ' +
          'for insurance, specifically {{riskDetail}}.',
        fields: [
          { key: Fields.riskGroup, placeholder: 'risk group' },
          { key: Fields.riskDetail, placeholder: 'risk detail' },
        ],
      },
    ],
  },
  [Sources.aps]: {
    id: Sources.aps,
    label: 'APS',
    icon: 'ti-stethoscope',
    paragraphs: [
      {
        text:
          'We are letting you know your life insurance coverage has been ' +
          'approved. However, we are approving the policy without the ' +
          '{{riders}} due to information contained in the medical records ' +
          'received from {{reference}}, which documents a history of ' +
          '{{diagnosis}}. To review your medical history please contact the ' +
          'doctor’s office for your medical record.',
        fields: [
          { key: Fields.reference, placeholder: 'doctor / facility' },
          { key: Fields.diagnosis, placeholder: 'diagnosis' },
        ],
      },
      {
        text:
          'You have the right to know the specific reason why your application ' +
          'was approved with a different premium rate. This may be requested by ' +
          'emailing Ethos **within 6 months** of the date of this letter at ' +
          '[accounts@ethoslife.com](mailto:accounts@ethoslife.com), one of our ' +
          'representatives will be glad to help.',
        fields: [],
      },
    ],
  },
  [Sources.eti]: {
    id: Sources.eti,
    label: 'ETI',
    icon: 'ti-mail-question',
    paragraphs: [
      {
        text:
          'We are letting you know your life insurance coverage has been ' +
          'approved. However, we are approving the policy without the ' +
          '{{riders}} due to information you provided on the {{risk}} ' +
          'questionnaire you completed.',
        fields: [{ key: Fields.risk, placeholder: 'questionnaire name' }],
      },
    ],
  },
  [Sources.rxOrMedClaimsMilliman]: {
    id: Sources.rxOrMedClaimsMilliman,
    label: 'Rx or Med Claims (Milliman)',
    icon: 'ti-pill',
    paragraphs: [
      {
        text:
          'During the application process, you authorized the release of your ' +
          'prescription history and/or diagnostic history to help us determine ' +
          'your eligibility for insurance. We are approving the policy without ' +
          'the {{riders}} due to information we received regarding your ' +
          'prescription history and/or diagnostic history.',
        fields: [],
      },
      {
        text:
          'To obtain a copy of your prescription history and/or diagnostic ' +
          'history at no charge to you, please contact IntelliScript **within ' +
          '60 days** from the date of this letter. You have the right to ' +
          'dispute any information contained in the report with IntelliScript. ' +
          'You may contact IntelliScript by mail at:',
        fields: [],
        contactAfter:
          'Milliman IntelliScript\n' +
          'P.O. Box 2223, Brookfield, WI 53008\n' +
          '[1-(877) 211-4816](tel:18772114816) [Tollfree]',
      },
      {
        text:
          'Note that IntelliScript did not make any decision in regard to your ' +
          'insurability, and they are not able to explain why the decision was ' +
          'made. However, you have the right to know the specific reason why ' +
          'your application was approved with a different premium rate. This ' +
          'may be requested by emailing Ethos **within 6 months** of the date ' +
          'of this letter at [accounts@ethoslife.com](mailto:accounts@ethoslife.com), ' +
          'one of our representatives will be glad to help.',
        fields: [],
      },
    ],
  },
  [Sources.custom]: {
    id: Sources.custom,
    label: 'Custom',
    icon: 'ti-pencil',
    paragraphs: [
      {
        text: '{{customContent}}',
        fields: [
          {
            key: Fields.customContent,
            placeholder: 'Enter custom paragraph text',
          },
        ],
      },
    ],
  },
};

export const OTAF_HIGHER_BLOCKS: LetterBlockSet = {
  [Sources.appInfoDisclosure]: {
    id: Sources.appInfoDisclosure,
    label: 'App info / Disclosure',
    icon: 'ti-file-description',
    paragraphs: [
      {
        text:
          'The difference between the quoted rate and the issued rate was ' +
          'influenced, in whole or in part, by medical history admitted on your ' +
          'application for insurance, specifically relating to your disclosed ' +
          'history of {{applicableQuestionnaire}}.',
        juvenileText:
          'The difference between the quoted rate and the issued rate was ' +
          'influenced, in whole or in part, by medical history admitted on your ' +
          'application for insurance, specifically relating to the proposed life ' +
          'insured\'s disclosed history of {{applicableQuestionnaire}}.',
        fields: [
          {
            key: Fields.applicableQuestionnaire,
            placeholder: 'questionnaire name',
          },
        ],
      },
    ],
  },
  [Sources.aps]: {
    id: Sources.aps,
    label: 'APS',
    icon: 'ti-stethoscope',
    paragraphs: [
      {
        text:
          'The difference between the quoted rate and the issued rate was ' +
          'influenced, in whole or in part, by information contained in the ' +
          'medical records received from {{medicalFacilityOrProvider}} reporting ' +
          'a history of {{ratableHistory}}. To review your medical history ' +
          'please contact the doctor\'s office for your medical record.',
        juvenileText:
          'The difference between the quoted rate and the issued rate was ' +
          'influenced, in whole or in part, by information contained in the ' +
          'medical records received from {{medicalFacilityOrProvider}} reporting ' +
          'a history of {{ratableHistory}}. To review the proposed life ' +
          'insured\'s medical history please contact the doctor\'s office for the ' +
          'proposed life insured\'s medical record.',
        fields: [
          {
            key: Fields.medicalFacilityOrProvider,
            placeholder: 'doctor / facility',
          },
          { key: Fields.ratableHistory, placeholder: 'condition' },
        ],
      },
    ],
  },
  [Sources.buildDisclosedOnApp]: {
    id: Sources.buildDisclosedOnApp,
    label: 'Build disclosed on app',
    icon: 'ti-user',
    paragraphs: [
      {
        text:
          'The difference between the quoted rate and the issued rate was based, ' +
          'in whole or in part, on your admitted height and weight ' +
          '({{height}} - {{weight}}lbs).',
        juvenileText:
          'The difference between the quoted rate and the issued rate was based, ' +
          'in whole or in part, on the proposed life insured\'s admitted height ' +
          'and weight ({{height}} - {{weight}} lbs).',
        fields: [
          { key: Fields.height, placeholder: 'height' },
          { key: Fields.weight, placeholder: 'weight' },
        ],
      },
    ],
  },
  [Sources.buildInAps]: {
    id: Sources.buildInAps,
    label: 'Build in APS',
    icon: 'ti-report-medical',
    paragraphs: [
      {
        text:
          'The difference between the quoted rate and the issued rate was based, ' +
          'in whole or in part, on your admitted height and weight ' +
          '({{height}} - {{weight}}lbs) as reported in your medical records from ' +
          '{{medicalFacilityOrProvider}} on {{dateMeasurementTaken}}. To review ' +
          'your medical history please contact the doctor\'s office for your ' +
          'medical record.',
        juvenileText:
          'The difference between the quoted rate and the issued rate was based, ' +
          'in whole or in part, on the proposed life insured\'s admitted height ' +
          'and weight ({{height}} - {{weight}} lbs) as reported in the proposed ' +
          'life insured\'s medical records from {{medicalFacilityOrProvider}} on ' +
          '{{dateMeasurementTaken}}. To review the proposed life insured\'s ' +
          'medical history please contact the doctor\'s office for the proposed ' +
          'life insured\'s medical record.',
        fields: [
          { key: Fields.height, placeholder: 'height' },
          { key: Fields.weight, placeholder: 'weight' },
          {
            key: Fields.medicalFacilityOrProvider,
            placeholder: 'facility name',
          },
          { key: Fields.dateMeasurementTaken, placeholder: 'date' },
        ],
      },
    ],
  },
  [Sources.eti]: {
    id: Sources.eti,
    label: 'ETI',
    icon: 'ti-mail-question',
    paragraphs: [
      {
        text:
          'The difference between the quoted rate and the issued rate was based, ' +
          'in whole or in part, by information you provided on the ' +
          '{{applicableQuestionnaire}} questionnaire you completed.',
        juvenileText:
          'The difference between the quoted rate and the issued rate was based, ' +
          'in whole or in part, by information you provided on the ' +
          '{{applicableQuestionnaire}} questionnaire you completed for the ' +
          'proposed life insured.',
        fields: [
          {
            key: Fields.applicableQuestionnaire,
            placeholder: 'questionnaire name',
          },
        ],
      },
    ],
  },
  [Sources.financial]: {
    id: Sources.financial,
    label: 'Financial',
    icon: 'ti-cash',
    paragraphs: [
      {
        text:
          'The difference between the applied for and approved coverage rate was ' +
          'influenced, in whole or in part, by income or in-force insurance ' +
          'coverage information disclosed on your application for life insurance.',
        juvenileText:
          'The difference between the amount applied for and approved coverage ' +
          'rate was influenced, in whole or in part, by income or in-force ' +
          'insurance coverage information disclosed on your application for life ' +
          'insurance for the proposed life insured.',
        fields: [],
      },
    ],
  },
  [Sources.labPiQture]: {
    id: Sources.labPiQture,
    label: 'LabPiQture',
    icon: 'ti-flask',
    paragraphs: [
      {
        text:
          'The difference between the quoted rate and the issued rate was based ' +
          'in part on information contained in prior lab results obtained from ' +
          'ExamOne.',
        fields: [],
      },
      {
        text:
          'To obtain a copy of your report at no charge to you, please contact ' +
          'ExamOne **within 60 days** of the date of this letter. Any questions ' +
          'regarding the accuracy or completeness of the information in your ' +
          'report should be directed to ExamOne. You may contact ExamOne by ' +
          'calling them at:',
        fields: [],
        contactAfter:
          'ExamOne Consumer Disclosure\n' + '[1-844-255-8047](tel:18442558047)',
      },
      {
        text:
          'Note that ExamOne did not make any decision in regard to your ' +
          'insurability and would not be able to explain why this decision was ' +
          'made. However, you have the right to know the specific reason why ' +
          'your application was approved with a different premium rate. This ' +
          'may be requested by emailing Ethos **within 6 months** of the date ' +
          'of this letter at [accounts@ethoslife.com](mailto:accounts@ethoslife.com), ' +
          'one of our representatives will be glad to help.',
        fields: [],
      },
    ],
    juvenileParagraphs: [
      {
        text:
          'The difference between the quoted rate and the issued rate was based ' +
          'in part on information contained in prior lab results obtained from ' +
          'ExamOne.',
        fields: [],
      },
      {
        text:
          'If you have activated coverage, no action is required. Your policy ' +
          'will continue to remain in force as long as you continue to pay your ' +
          'current premiums as scheduled. If you have not activated coverage, ' +
          'you can do so by paying your initial premium. Once your policy has ' +
          'been activated, your premium payment amounts will remain as shown on ' +
          'your policy schedule.',
        fields: [],
      },
      {
        text:
          'To obtain a copy of the proposed life insured\'s report at no charge ' +
          'to you, please contact ExamOne **within 60 days** of the date of this ' +
          'letter. Any questions regarding the accuracy or completeness of the ' +
          'information in the proposed life insured\'s report should be ' +
          'directed to ExamOne. You may contact ExamOne by calling them at:',
        fields: [],
        contactAfter:
          'ExamOne Consumer Disclosure\n' + '[1-844-255-8047](tel:18442558047)',
      },
      {
        text:
          'Note that ExamOne did not make any decision in regard to the ' +
          'proposed life insured\'s insurability and would not be able to ' +
          'explain why this decision was made. However, you have the right to ' +
          'know the specific reason why your application was approved with a ' +
          'different premium rate. This may be requested by emailing Ethos ' +
          '**within 6 months** of the date of this letter at ' +
          '[accounts@ethoslife.com](mailto:accounts@ethoslife.com), one of our ' +
          'representatives will be glad to help.',
        fields: [],
      },
    ],
  },
  [Sources.unresolvedMib]: {
    id: Sources.unresolvedMib,
    label: 'MIB',
    icon: 'ti-database',
    paragraphs: [
      {
        text: 'Our decision was based on information provided to us by MIB.',
        fields: [],
      },
      {
        text:
          'During the application process, we gave you a written description ' +
          'of the MIB, which is a consumer reporting agency that operates an ' +
          'information exchange on behalf of insurers. This authorization ' +
          'informed you that the MIB will arrange disclosure of any information ' +
          'it may have in your file. When we evaluated your application, the ' +
          'MIB sent a brief, coded report about you, to us, as you authorized ' +
          'in your application.',
        fields: [],
      },
      {
        text:
          'To obtain a copy of your MIB Consumer File at no charge to you, ' +
          'please contact MIB **within 60 days** of the date of this letter. ' +
          'You may contact MIB Information Office by mail, phone or online ' +
          'requests at:',
        fields: [],
        contactAfter:
          'MIB\n' +
          '50 Braintree Hill Park, Suite 400\n' +
          'Braintree, MA 02184-8734\n' +
          '[1-866-692-6901](tel:18666926901)\n' +
          '[www.mib.com](https://www.mib.com)',
      },
      {
        text:
          'Please be aware that the Medical Information Bureau (MIB) only ' +
          'provides consumer related data and/or information. This agency is ' +
          'not involved in, nor did they make any, underwriting decisions ' +
          'regarding your application.',
        fields: [],
      },
      {
        text:
          'However, you have the right to know the specific reason why your ' +
          'application was approved with a different premium rate. This may be ' +
          'requested by emailing Ethos **within 6 months** of the date of this ' +
          'letter at ' +
          '[accounts@ethoslife.com](mailto:accounts@ethoslife.com), one of our ' +
          'representatives will be glad to help.',
        fields: [],
      },
    ],
  },
  [Sources.mibAppEti]: {
    id: Sources.mibAppEti,
    label: 'MIB + ETI',
    icon: 'ti-database',
    paragraphs: [
      {
        text:
          'The difference between the quoted rate and the issued rate was ' +
          'based, in whole or in part, by information you provided on the ' +
          '{{applicableQuestionnaire}} questionnaire you completed.',
        juvenileText:
          'The difference between the quoted rate and the issued rate was ' +
          'based, in whole or in part, by information you provided on the ' +
          '{{applicableQuestionnaire}} questionnaire you completed for the ' +
          'proposed life insured.',
        fields: [
          {
            key: Fields.applicableQuestionnaire,
            placeholder: 'questionnaire name',
          },
        ],
      },
      {
        text:
          'In addition, our decision was based in part on information obtained ' +
          'in the consumer file received by the Medical Information Bureau ' +
          '(MIB). During the application process, we gave you a written ' +
          'description of the MIB, which is a consumer reporting agency that ' +
          'operates an information exchange on behalf of insurers. This ' +
          'authorization informed you that the MIB will arrange disclosure of ' +
          'any information it may have in your file. When we evaluated your ' +
          'application, the MIB sent a brief, coded report about you, to us, as ' +
          'you authorized in your application.',
        juvenileText:
          'In addition, our decision was based in part on information obtained ' +
          'in the consumer file received by the Medical Information Bureau ' +
          '(MIB). During the application process, we gave you a written ' +
          'description of the MIB, which is a consumer reporting agency that ' +
          'operates an information exchange on behalf of insurers. This ' +
          'authorization informed you that the MIB will arrange disclosure of ' +
          'any information it may have in the proposed life insured\'s file. ' +
          'When we evaluated your application, the MIB sent a brief, coded ' +
          'report about the proposed life insured, to us, as you authorized in ' +
          'your application.',
        fields: [],
      },
      {
        text:
          'To obtain a copy of your MIB Consumer File at no charge to you, ' +
          'please contact MIB **within 60 days** of the date of this letter. ' +
          'You may contact MIB Information Office by mail, phone or online ' +
          'requests at:',
        juvenileText:
          'To obtain a copy of the proposed life insured\'s MIB Consumer File ' +
          'at no charge to you, please contact MIB **within 60 days** of the ' +
          'date of this letter. You may contact MIB Information Office by mail, ' +
          'phone or online requests at:',
        fields: [],
        contactAfter:
          'MIB\n' +
          '50 Braintree Hill Park, Suite 400\n' +
          'Braintree, MA 02184-8734\n' +
          '[1-866-692-6901](tel:18666926901)\n' +
          '[www.mib.com](https://www.mib.com)',
      },
    ],
  },
  [Sources.mibAps]: {
    id: Sources.mibAps,
    label: 'MIB + APS',
    icon: 'ti-database',
    paragraphs: [
      {
        text:
          'The difference between the quoted rate and the issued rate was ' +
          'influenced, in whole or in part, by information contained in the ' +
          'medical records received from {{medicalFacilityOrProvider}} reporting ' +
          'a history of {{ratableHistory}}. To review your medical history, ' +
          'please contact the doctor\'s office for your medical records.',
        juvenileText:
          'The difference between the quoted rate and the issued rate was ' +
          'influenced, in whole or in part, by information contained in the ' +
          'medical records received from {{medicalFacilityOrProvider}} reporting ' +
          'a history of {{ratableHistory}}. To review the proposed life ' +
          'insured\'s medical history, please contact the doctor\'s office for ' +
          'the proposed life insured\'s medical records.',
        fields: [
          {
            key: Fields.medicalFacilityOrProvider,
            placeholder: 'doctor / facility',
          },
          { key: Fields.ratableHistory, placeholder: 'condition' },
        ],
      },
      {
        text:
          'In addition, our decision was based in part on information obtained ' +
          'in the consumer file received by the Medical Information Bureau ' +
          '(MIB). During the application process, we gave you a written ' +
          'description of the MIB, which is a consumer reporting agency that ' +
          'operates an information exchange on behalf of insurers. This ' +
          'authorization informed you that the MIB will arrange disclosure of ' +
          'any information it may have in your file. When we evaluated your ' +
          'application, the MIB sent a brief, coded report about you, to us, as ' +
          'you authorized in your application.',
        juvenileText:
          'In addition, our decision was based in part on information obtained ' +
          'in the consumer file received by the Medical Information Bureau ' +
          '(MIB). During the application process, we gave you a written ' +
          'description of the MIB, which is a consumer reporting agency that ' +
          'operates an information exchange on behalf of insurers. This ' +
          'authorization informed you that the MIB will arrange disclosure of ' +
          'any information it may have in the proposed life insured\'s file. ' +
          'When we evaluated your application, the MIB sent a brief, coded ' +
          'report about the proposed life insured, to us, as you authorized in ' +
          'your application.',
        fields: [],
      },
      {
        text:
          'To obtain a copy of your MIB Consumer File at no charge to you, ' +
          'please contact MIB **within 60 days** of the date of this letter. ' +
          'You may contact MIB Information Office by mail, phone or online ' +
          'requests at:',
        juvenileText:
          'To obtain a copy of the proposed life insured\'s MIB Consumer File ' +
          'at no charge to you, please contact MIB **within 60 days** of the ' +
          'date of this letter. You may contact MIB Information Office by mail, ' +
          'phone or online requests at:',
        fields: [],
        contactAfter:
          'MIB\n' +
          '50 Braintree Hill Park, Suite 400\n' +
          'Braintree, MA 02184-8734\n' +
          '[1-866-692-6901](tel:18666926901)\n' +
          '[www.mib.com](https://www.mib.com)',
      },
    ],
  },
  [Sources.mibLabPiQture]: {
    id: Sources.mibLabPiQture,
    label: 'MIB + LabPiQture',
    icon: 'ti-database',
    paragraphs: [
      {
        text:
          'The difference between the quoted rate and the issued rate was based ' +
          'in part on information contained in prior lab results obtained from ' +
          'ExamOne.',
        fields: [],
      },
      {
        text:
          'To obtain a copy of your report at no charge to you, please contact ' +
          'ExamOne **within 60 days** of the date of this letter. Any questions ' +
          'regarding the accuracy or completeness of the information in your ' +
          'report should be directed to ExamOne. You may contact ExamOne by ' +
          'calling them at:',
        fields: [],
        contactAfter:
          'ExamOne Consumer Disclosure\n' + '[1-844-255-8047](tel:18442558047)',
      },
      {
        text:
          'During the application process, we gave you a written description of ' +
          'the MIB, which is a consumer reporting agency that operates an ' +
          'information exchange on behalf of insurers. This authorization ' +
          'informed you that the MIB will arrange disclosure of any information ' +
          'it may have in your file. When we evaluated your application, the ' +
          'MIB sent a brief, coded report about you, to us, as you authorized ' +
          'in your application.',
        fields: [],
      },
      {
        text:
          'To obtain a copy of your MIB Consumer File at no charge to you, ' +
          'please contact MIB **within 60 days** of the date of this letter. ' +
          'You may contact MIB Information Office by mail, phone or online ' +
          'requests at:',
        fields: [],
        contactAfter:
          'MIB\n' +
          '50 Braintree Hill Park, Suite 400\n' +
          'Braintree, MA 02184-8734\n' +
          '[1-866-692-6901](tel:18666926901)\n' +
          '[www.mib.com](https://www.mib.com)',
      },
      {
        text:
          'You have the right to know the specific reason why your application ' +
          'was approved with a different premium rate. This may be requested by ' +
          'emailing Ethos **within 6 months** of the date of this letter at ' +
          '[accounts@ethoslife.com](mailto:accounts@ethoslife.com), one of our ' +
          'representatives will be glad to help.',
        fields: [],
      },
    ],
    juvenileParagraphs: [
      {
        text:
          'The difference between the quoted rate and the issued rate was based ' +
          'in part on information contained in prior lab results of the ' +
          'proposed life insured obtained from ExamOne.',
        fields: [],
      },
      {
        text:
          'If you have activated coverage, no action is required. Your policy ' +
          'will continue to remain in force as long as you continue to pay your ' +
          'current premiums as scheduled. If you have not activated coverage, ' +
          'you can do so by paying your initial premium. Once your policy has ' +
          'been activated, your premium payment amounts will remain as shown on ' +
          'your policy schedule.',
        fields: [],
      },
      {
        text:
          'To obtain a copy of the proposed life insured\'s report at no charge ' +
          'to you, please contact ExamOne **within 60 days** of the date of this ' +
          'letter. Any questions regarding the accuracy or completeness of the ' +
          'information in the proposed life insured\'s report should be ' +
          'directed to ExamOne. You may contact ExamOne by calling them at:',
        fields: [],
        contactAfter:
          'ExamOne Consumer Disclosure\n' + '[1-844-255-8047](tel:18442558047)',
      },
      {
        text:
          'During the application process, we gave you a written description of ' +
          'the MIB, which is a consumer reporting agency that operates an ' +
          'information exchange on behalf of insurers. This authorization ' +
          'informed you that the MIB will arrange disclosure of any information ' +
          'it may have in the proposed life insured\'s file. When we evaluated ' +
          'your application, the MIB sent a brief, coded report about the ' +
          'proposed life insured, to us, as you authorized in your application.',
        fields: [],
      },
      {
        text:
          'To obtain a copy of the proposed life insured\'s MIB Consumer File ' +
          'at no charge to you, please contact MIB **within 60 days** of the ' +
          'date of this letter. You may contact MIB Information Office by mail, ' +
          'phone or online requests at:',
        fields: [],
        contactAfter:
          'MIB\n' +
          '50 Braintree Hill Park, Suite 400\n' +
          'Braintree, MA 02184-8734\n' +
          '[1-866-692-6901](tel:18666926901)\n' +
          '[www.mib.com](https://www.mib.com)',
      },
      {
        text:
          'You have the right to know the specific reason why your application ' +
          'was approved with a different premium rate. This may be requested by ' +
          'emailing Ethos **within 6 months** of the date of this letter at ' +
          '[accounts@ethoslife.com](mailto:accounts@ethoslife.com), one of our ' +
          'representatives will be glad to help.',
        fields: [],
      },
    ],
  },
  [Sources.mvr]: {
    id: Sources.mvr,
    label: 'MVR',
    icon: 'ti-car',
    paragraphs: [
      {
        text:
          'During the application process, you authorized the release of your ' +
          'motor vehicle history to help us determine your eligibility for ' +
          'insurance. The difference between the quoted rate and the issued ' +
          'rate was based, in whole or in part, on information we received ' +
          'regarding your Motor Vehicle Report. This information was obtained ' +
          'by TransUnion Driver\'s Risk, you have the right to dispute any ' +
          'information contained in the report with TransUnion.',
        juvenileText:
          'During the application process, you authorized the release of the ' +
          'proposed life insured\'s motor vehicle history to help us determine ' +
          'the proposed life insured\'s eligibility for insurance. The ' +
          'difference between the quoted rate and the issued rate was based, ' +
          'in whole or in part, on information we received regarding the ' +
          'proposed life insured\'s Motor Vehicle Report. This information was ' +
          'obtained by TransUnion Driver\'s Risk, you have the right to dispute ' +
          'any information contained in the report with TransUnion.',
        fields: [],
      },
      {
        text:
          'To obtain a copy of your motor vehicle record report at no charge to ' +
          'you, please contact the TransUnion Consumer Service Center **within ' +
          '60 days** from the date of this letter. They are located at:',
        fields: [],
        contactAfter:
          'Datalink Consumer Relations\n' +
          '[supportCDR@transunion.com](mailto:supportCDR@transunion.com)\n' +
          '[866-454-3238](tel:8664543238)\n' +
          'P.O. Box 340639\n' +
          'Sacramento, CA 95834\n',
      },
      {
        text:
          'Note that TransUnion did not make this decision in regard to your ' +
          'insurability and would not be able to explain why the decision was ' +
          'made. However, you have the right to know the specific reason why ' +
          'your application was approved with a different premium rate. This ' +
          'may be requested by emailing Ethos **within 6 months** of the date ' +
          'of this letter at [accounts@ethoslife.com](mailto:accounts@ethoslife.com), ' +
          'one of our representatives will be glad to help.',
        juvenileText:
          'Note that TransUnion did not make this decision in regard to the ' +
          'proposed life insured\'s insurability and would not be able to ' +
          'explain why the decision was made. However, you have the right to ' +
          'know the specific reason why your application was approved with a ' +
          'different premium rate. This may be requested by emailing Ethos ' +
          '**within 6 months** of the date of this letter at ' +
          '[accounts@ethoslife.com](mailto:accounts@ethoslife.com), one of our ' +
          'representatives will be glad to help.',
        fields: [],
      },
    ],
  },
  [Sources.rxOrMedClaimsMilliman]: {
    id: Sources.rxOrMedClaimsMilliman,
    label: 'Rx or Med Claims (Milliman)',
    icon: 'ti-pill',
    paragraphs: [
      {
        text:
          'During the application process, you authorized the release of your ' +
          'prescription and diagnostic history to help us determine your ' +
          'eligibility for insurance. The difference between the quoted rate ' +
          'and the issued rate was based, in whole or in part, on information ' +
          'regarding your prescription drug and/or diagnostic history.',
        juvenileText:
          'During the application process, you authorized the release of the ' +
          'proposed life insured\'s prescription and diagnostic history to help ' +
          'us determine the proposed life insured\'s eligibility for insurance. ' +
          'The difference between the quoted rate and the issued rate was ' +
          'based, in whole or in part, on information we received regarding ' +
          'the proposed life insured\'s prescription drug history.',
        fields: [],
      },
      {
        text:
          'To obtain a copy of your prescription and/or diagnostic history ' +
          'report at no charge to you, please contact IntelliScript **within ' +
          '60 days** from the date of this letter. You have the right to ' +
          'dispute any information contained in the report with IntelliScript. ' +
          'You may contact IntelliScript by mail at:',
        juvenileText:
          'To obtain a copy of the proposed life insured\'s prescription and/or ' +
          'diagnostic history report at no charge to you, please contact ' +
          'Milliman IntelliScript **within 60 days** from the date of this ' +
          'letter. You have the right to dispute any information contained in ' +
          'the report with IntelliScript. You may contact Milliman ' +
          'IntelliScript by mail at:',
        fields: [],
        contactAfter:
          'Milliman IntelliScript\n' +
          'P.O. Box 2223, Brookfield, WI 53008\n' +
          '[1-(877) 211-4816](tel:18772114816) [Tollfree]',
        juvenileContactAfter:
          'Milliman IntelliScript\n' +
          'Website: [www.rxhistories.com/for-consumers](https://www.rxhistories.com/for-consumers)\n' +
          'Email: [FCRAReport@milliman.com](mailto:FCRAReport@milliman.com)\n' +
          'P.O. Box 2223, Brookfield, WI 53008\n' +
          'Toll-free: [(877) 211-4816](tel:18772114816)',
      },
      {
        text:
          'Note that IntelliScript did not make this decision and they are not ' +
          'able to explain why the decision was made. However, you have the ' +
          'right to know the specific reason why your application was approved ' +
          'with a different premium rate. This may be requested by emailing ' +
          'Ethos **within 6 months** of the date of this letter at ' +
          '[accounts@ethoslife.com](mailto:accounts@ethoslife.com), one of our ' +
          'representatives will be glad to help.',
        fields: [],
      },
    ],
  },
  [Sources.rxOrMedClaimsExamOne]: {
    id: Sources.rxOrMedClaimsExamOne,
    label: 'Rx or Med Claims (ExamOne)',
    icon: 'ti-pill',
    paragraphs: [
      {
        text:
          'During the application process, you authorized the release of your ' +
          'prescription and diagnostic history to help us determine your ' +
          'eligibility for insurance. The difference between the quoted rate ' +
          'and the issued rate was based, in whole or in part, on information ' +
          'regarding your prescription drug and/or diagnostic history.',
        juvenileText:
          'During the application process, you authorized the release of the ' +
          'proposed life insured\'s prescription and diagnostic history to help ' +
          'us determine the proposed life insured\'s eligibility for insurance. ' +
          'The difference between the quoted rate and the issued rate was ' +
          'based, in whole or in part, on information we received regarding ' +
          'the proposed life insured\'s prescription drug history.',
        fields: [],
      },
      {
        text:
          'To obtain a copy of your prescription and/or diagnostic history ' +
          'report at no charge to you, please contact ExamOne **within 60 ' +
          'days** of the date of this letter. Any questions regarding the ' +
          'accuracy or completeness of the information in your report should be ' +
          'directed to ExamOne. You may contact ExamOne by calling them at:',
        juvenileText:
          'To obtain a copy of the proposed life insured\'s prescription and/or ' +
          'diagnostic history report at no charge to you, please contact ' +
          'ExamOne **within 60 days** of the date of this letter. Any questions ' +
          'regarding the accuracy or completeness of the information in the ' +
          'proposed life insured\'s report should be directed to ExamOne. You ' +
          'may contact ExamOne by calling them at:',
        fields: [],
        contactAfter:
          'ExamOne Consumer Disclosure\n' + '[1-844-255-8047](tel:18442558047)',
      },
      {
        text:
          'Note that ExamOne did not make any decision in regard to your ' +
          'insurability and would not be able to explain why this decision was ' +
          'made. However, you have the right to know the specific reason why ' +
          'your application was approved with a different premium rate. This ' +
          'may be requested by emailing Ethos **within 6 months** of the date ' +
          'of this letter at [accounts@ethoslife.com](mailto:accounts@ethoslife.com), ' +
          'one of our representatives will be glad to help.',
        juvenileText:
          'Note that ExamOne did not make any decision in regard to the ' +
          'proposed life insured\'s insurability and would not be able to ' +
          'explain why this decision was made. However, you have the right to ' +
          'know the specific reason why your application was approved with a ' +
          'different premium rate. This may be requested by emailing Ethos ' +
          '**within 6 months** of the date of this letter at ' +
          '[accounts@ethoslife.com](mailto:accounts@ethoslife.com), one of our ' +
          'representatives will be glad to help.',
        fields: [],
      },
    ],
  },
  [Sources.trl]: {
    id: Sources.trl,
    label: 'TRL',
    icon: 'ti-chart-bar',
    paragraphs: [
      {
        text:
          'The difference between the quoted rate and the issued rate was based ' +
          'in part on information contained in the consumer report received by ' +
          'TransUnion.',
        fields: [],
      },
      {
        text:
          'To obtain a copy of your report at no charge to you, please contact ' +
          'TransUnion **within 60 days** of the date of this letter. Any ' +
          'questions regarding the accuracy or completeness of the information ' +
          'in your report should be directed to TransUnion. You may contact ' +
          'TransUnion by mail at:',
        juvenileText:
          'To obtain a copy of the proposed life insured\'s report at no charge ' +
          'to you, please contact TransUnion **within 60 days** of the date of ' +
          'this letter. Any questions regarding the accuracy or completeness of ' +
          'the information in the proposed life insured\'s report should be ' +
          'directed to TransUnion. You may contact TransUnion by mail at:',
        fields: [],
        contactAfter:
          'TransUnion Consumer Disclosure\n' +
          'PO Box 2000\n' +
          'Chester, PA 19016-2000\n' +
          '[1-800-916-8800](tel:18009168800)\n' +
          '[www.transunion.com](https://www.transunion.com)',
      },
      {
        text:
          'Note that TransUnion did not make any decision in regard to your ' +
          'insurability and would not be able to explain why this decision was ' +
          'made. However, you have the right to know the specific reason why ' +
          'your application was approved with a different premium rate. This ' +
          'may be requested by emailing Ethos **within 6 months** of the date ' +
          'of this letter at [accounts@ethoslife.com](mailto:accounts@ethoslife.com), ' +
          'one of our representatives will be glad to help.',
        juvenileText:
          'Note that TransUnion did not make any decision in regard to the ' +
          'proposed life insured\'s insurability and would not be able to ' +
          'explain why this decision was made. However, you have the right to ' +
          'know the specific reason why your application was approved with a ' +
          'different premium rate. This may be requested by emailing Ethos ' +
          '**within 6 months** of the date of this letter at ' +
          '[accounts@ethoslife.com](mailto:accounts@ethoslife.com), one of our ' +
          'representatives will be glad to help.',
        fields: [],
      },
    ],
  },
  [Sources.rxEti]: {
    id: Sources.rxEti,
    label: 'Rx ETI',
    icon: 'ti-pill',
    paragraphs: [
      {
        text:
          'The difference between the quoted rate and the issued rate was ' +
          'based, in whole or in part, by information you provided on the ' +
          '{{applicableQuestionnaire}} questionnaire you completed.',
        fields: [
          {
            key: Fields.applicableQuestionnaire,
            placeholder: 'questionnaire name',
          },
        ],
      },
      {
        text:
          'During the application process, you authorized the release of your ' +
          'prescription and diagnostic history to help us determine your ' +
          'eligibility for insurance. The difference between the quoted rate ' +
          'and the issued rate was based, in whole or in part, on information ' +
          'regarding your prescription drug and/or diagnostic history.',
        fields: [],
      },
      {
        text:
          'To obtain a copy of your prescription and/or diagnostic history ' +
          'report at no charge to you, please contact IntelliScript **within ' +
          '60 days** from the date of this letter. You have the right to ' +
          'dispute any information contained in the report with IntelliScript. ' +
          'You may contact IntelliScript by mail at:',
        fields: [],
        contactAfter:
          'Milliman IntelliScript\n' +
          'P.O. Box 2223, Brookfield, WI 53008\n' +
          '[1-(877) 211-4816](tel:18772114816) [Tollfree]',
      },
      {
        text:
          'Note that IntelliScript did not make this decision and they are not ' +
          'able to explain why the decision was made. However, you have the ' +
          'right to know the specific reason why your application was approved ' +
          'with a different premium rate. This may be requested by emailing ' +
          'Ethos **within 6 months** of the date of this letter at ' +
          '[accounts@ethoslife.com](mailto:accounts@ethoslife.com), one of our ' +
          'representatives will be glad to help.',
        fields: [],
      },
    ],
    juvenileParagraphs: [
      {
        text:
          'If you have activated coverage, no action is required. Your policy ' +
          'will continue to remain in force as long as you continue to pay your ' +
          'current premiums as scheduled. If you have not activated coverage, ' +
          'you can do so by paying your initial premium. Once your policy has ' +
          'been activated, your premium payment amounts will remain as shown on ' +
          'your policy schedule.',
        fields: [],
      },
      {
        text:
          'The difference between the quoted rate and the issued rate was ' +
          'based, in whole or in part, by information you provided on the ' +
          '{{applicableQuestionnaire}} questionnaire you completed for the ' +
          'proposed life insured. Also considered was information we received ' +
          'regarding the proposed life insured\'s prescription drug history.',
        fields: [
          {
            key: Fields.applicableQuestionnaire,
            placeholder: 'questionnaire name',
          },
        ],
      },
      {
        text:
          'To obtain a copy of the proposed life insured\'s prescription drug ' +
          'and/or diagnostic history at no charge to you, please contact ' +
          'Milliman IntelliScript **within 60 days** from the date of this ' +
          'letter. You have the right to dispute any information contained in ' +
          'the report with IntelliScript. You may contact Milliman ' +
          'IntelliScript by mail at:',
        fields: [],
        contactAfter:
          'Milliman IntelliScript\n' +
          'Website: [www.rxhistories.com/for-consumers](https://www.rxhistories.com/for-consumers)\n' +
          'Email: [FCRAReport@milliman.com](mailto:FCRAReport@milliman.com)\n' +
          'P.O. Box 2223, Brookfield, WI 53008\n' +
          'Toll-free: [(877) 211-4816](tel:18772114816)',
      },
      {
        text:
          'Note that Milliman IntelliScript did not participate in our ' +
          'decision and they are not able to explain why the decision was ' +
          'made. However, you have the right to know the specific reason why ' +
          'your application was approved with a different premium rate. This ' +
          'may be requested by emailing Ethos at ' +
          '[accounts@ethoslife.com](mailto:accounts@ethoslife.com), one of our ' +
          'representatives will be glad to help.',
        fields: [],
      },
    ],
  },
  [Sources.mibInsuranceLabs]: {
    id: Sources.mibInsuranceLabs,
    label: 'Insurance Labs',
    icon: 'ti-database',
    paragraphs: [
      {
        text:
          'Our decision was based on lab testing performed ' +
          '{{dateMeasurementTaken}} for the purpose of underwriting an ' +
          'insurance application for another carrier{{information}}.',
        fields: [
          { key: Fields.dateMeasurementTaken, placeholder: 'date' },
          {
            key: Fields.information,
            placeholder: 'carrier name (optional, include space and parentheses)',
          },
        ],
      },
      {
        text:
          'During the application process, we gave you a written description ' +
          'of the MIB, a consumer reporting agency that operates an ' +
          'information exchange on behalf of insurers, together with a mailing ' +
          'address and telephone number of MIB and informed you that the MIB ' +
          'will arrange disclosure of any information it may have in your ' +
          'file. When we evaluated your application, the MIB sent a brief, ' +
          'coded report about you, to us, as you authorized in your ' +
          'application.',
        fields: [],
      },
      {
        text:
          'To obtain a copy of your MIB Consumer File at no charge to you, ' +
          'please contact MIB **within 60 days** of the date of this letter. ' +
          'You may contact MIB Information Office by mail, phone or online ' +
          'requests at:',
        fields: [],
        contactAfter:
          'MIB\n' +
          '50 Braintree Hill Park, Suite 400\n' +
          'Braintree, MA 02184-8734\n' +
          '[1-866-692-6901](tel:18666926901)\n' +
          '[www.mib.com](https://www.mib.com)',
      },
      {
        text:
          'You have the right to know the specific reason why your application ' +
          'was approved with a different premium rate. This may be requested ' +
          'by emailing Ethos **within 6 months** of the date of this letter ' +
          'at [accounts@ethoslife.com](mailto:accounts@ethoslife.com), one of ' +
          'our representatives will be glad to help.',
        fields: [],
      },
    ],
    juvenileParagraphs: [
      {
        text:
          'If you have activated coverage, no action is required. Your policy ' +
          'will continue to remain in force as long as you continue to pay your ' +
          'current premiums as scheduled. If you have not activated coverage, ' +
          'you can do so by paying your initial premium. Once your policy has ' +
          'been activated, your premium payment amounts will remain as shown on ' +
          'your policy schedule.',
        fields: [],
      },
      {
        text:
          'Our decision was based on lab testing performed ' +
          '{{dateMeasurementTaken}} for the purpose of underwriting an ' +
          'insurance application for another carrier{{information}}.',
        fields: [
          { key: Fields.dateMeasurementTaken, placeholder: 'date' },
          {
            key: Fields.information,
            placeholder: 'carrier name (optional, include space and parentheses)',
          },
        ],
      },
      {
        text:
          'During the application process, we gave you a written description ' +
          'of the MIB, which is a consumer reporting agency that operates an ' +
          'information exchange on behalf of insurers. This authorization ' +
          'informed you that the MIB will arrange disclosure of any information ' +
          'it may have in the proposed life insured\'s file. When we evaluated ' +
          'your application, the MIB sent a brief, coded report about the ' +
          'proposed life insured, to us, as you authorized in your application.',
        fields: [],
      },
      {
        text:
          'To obtain a copy of the proposed life insured\'s MIB Consumer File ' +
          'at no charge to you, please contact MIB **within 60 days** of the ' +
          'date of this letter. You may contact MIB Information Office by ' +
          'mail, phone or online requests at:',
        fields: [],
        contactAfter:
          'MIB\n' +
          '50 Braintree Hill Park, Suite 400\n' +
          'Braintree, MA 02184-8734\n' +
          '[1-866-692-6901](tel:18666926901)\n' +
          '[www.mib.com](https://www.mib.com)',
      },
      {
        text:
          'You have the right to know the specific reason why your application ' +
          'was approved with a different premium rate. This may be requested ' +
          'by emailing Ethos at ' +
          '[accounts@ethoslife.com](mailto:accounts@ethoslife.com), one of our ' +
          'representatives will be glad to help.',
        fields: [],
      },
    ],
  },
  [Sources.custom]: {
    id: Sources.custom,
    label: 'Custom',
    icon: 'ti-pencil',
    paragraphs: [
      {
        text: '{{customContent}}',
        fields: [
          {
            key: Fields.customContent,
            placeholder: 'Enter custom paragraph text',
          },
        ],
      },
    ],
  },
};

export const DECLINE_BLOCKS: LetterBlockSet = {
  [Sources.appInfoDisclosure]: {
    id: Sources.appInfoDisclosure,
    label: 'App info / Disclosure',
    icon: 'ti-file-description',
    paragraphs: [
      {
        text:
          'Our decision was based on information admitted on your application ' +
          'for insurance. This information, based on our guidelines, ' +
          'disqualifies approval of your life insurance application.',
        juvenileText:
          'Our decision was based on the proposed life insured’s medical ' +
          'history admitted on your application for insurance. This ' +
          'information, based on our guidelines, disqualifies approval of your ' +
          'life insurance application.',
        fields: [],
      },
    ],
  },
  [Sources.aps]: {
    id: Sources.aps,
    label: 'APS',
    icon: 'ti-stethoscope',
    paragraphs: [
      {
        text:
          'Our decision was based, in whole or in part, by information ' +
          'contained in the medical records received from ' +
          '{{medicalFacilityOrProvider}} reporting a history of ' +
          '{{declinableHistory}}. This information, based on our guidelines, ' +
          'disqualifies approval of your life insurance application. To review ' +
          'your medical history please contact the doctor’s office for your ' +
          'medical record.',
        juvenileText:
          'Our decision was based, in whole or in part, by information ' +
          'contained in the medical records received from ' +
          '{{medicalFacilityOrProvider}} reporting a history of ' +
          '{{declinableHistory}}. This information, based on our guidelines, ' +
          'disqualifies approval of your life insurance application. To review ' +
          'the medical history used in this assessment, we recommend ' +
          'contacting the medical provider’s office directly to obtain a copy ' +
          'of the records of proposed life insured.',
        fields: [
          {
            key: Fields.medicalFacilityOrProvider,
            placeholder: 'doctor / facility',
          },
          { key: Fields.declinableHistory, placeholder: 'condition' },
        ],
      },
    ],
  },
  [Sources.buildDisclosedOnApp]: {
    id: Sources.buildDisclosedOnApp,
    label: 'Build disclosed on app',
    icon: 'ti-user',
    paragraphs: [
      {
        text:
          'Our decision was based in whole or in part, on your admitted height ' +
          'and weight. This information, based on our guidelines, disqualifies ' +
          'approval of your life insurance application.',
        juvenileText:
          'Our decision was based in whole or in part, on the proposed life ' +
          'insured’s height and weight. This information, based on our ' +
          'guidelines, disqualifies approval of the proposed life insured’s ' +
          'life insurance application.',
        fields: [],
      },
    ],
  },
  [Sources.buildInAps]: {
    id: Sources.buildInAps,
    label: 'Build in APS',
    icon: 'ti-report-medical',
    paragraphs: [
      {
        text:
          'Our decision was based, in whole or in part, on your height and ' +
          'weight ({{height}} - {{weight}}lbs) as reported in your medical ' +
          'records from {{medicalFacilityOrProvider}} on ' +
          '{{dateMeasurementTaken}}. To review your medical history please ' +
          'contact the doctor’s office for your medical record.',
        juvenileText:
          'Our decision was based, in whole or in part, on the proposed life ' +
          'insured’s height and weight ({{height}} - {{weight}}lbs) as ' +
          'reported in the proposed life insured’s medical records from ' +
          '{{medicalFacilityOrProvider}} on {{dateMeasurementTaken}}. To ' +
          'review the medical history used in this assessment, we recommend ' +
          'contacting the medical provider’s office directly to obtain a copy ' +
          'of the records of proposed life insured.',
        fields: [
          { key: Fields.height, placeholder: 'height' },
          { key: Fields.weight, placeholder: 'weight' },
          {
            key: Fields.medicalFacilityOrProvider,
            placeholder: 'facility name',
          },
          { key: Fields.dateMeasurementTaken, placeholder: 'date' },
        ],
      },
    ],
  },
  [Sources.criminalHistory]: {
    id: Sources.criminalHistory,
    label: 'Criminal History',
    icon: 'ti-gavel',
    paragraphs: [
      {
        text:
          '{{carrierDisplayName}} appreciated the opportunity to consider your ' +
          'application for life insurance, but the circumstances are such that ' +
          'we are unable to approve the coverage applied for. Our action was ' +
          'influenced by information provided in a consumer report. (The ' +
          'consumer reporting agency did not take the adverse action, they are ' +
          'unable to furnish the reasons why the adverse action occurred). You ' +
          'have the right to obtain a free copy of the report **within 60 days** ' +
          'of receiving notice of the adverse action and the right to dispute ' +
          'the completeness and accuracy of the information contained in the ' +
          'consumer report. To exercise these rights, contact the consumer ' +
          'reporting agency below:',
        fields: [],
        contactAfter:
          'Explore Data\n' +
          '[(800) 531-9125](tel:8005319125)\n' +
          '[exploredata.com/consumer-inquiries/](https://www.exploredata.com/consumer-inquiries/)\n' +
          '2750 Blue Water Road, #200\n' +
          'Eagan, MN 55121',
      },
    ],
    juvenileParagraphs: [
      {
        text:
          'Our action was influenced by information provided in a consumer ' +
          'report. You have the right to obtain a free copy of the report ' +
          '**within 60 days** of receiving notice of the adverse action and ' +
          'the right to dispute the completeness and accuracy of the ' +
          'information contained in the consumer report. To exercise these ' +
          'rights, contact the consumer reporting agency below:',
        fields: [],
        contactAfter:
          'Explore Data\n' +
          '2750 Blue Water Road, #200\n' +
          'Eagan, MN 55121\n' +
          '[(800) 531-9125](tel:8005319125)\n' +
          '[exploredata.com/consumer-inquiries/](https://www.exploredata.com/consumer-inquiries/)',
      },
      {
        text:
          'Note that the consumer agency did not make any decision in regard ' +
          'to the proposed life insured’s insurability and would not be able ' +
          'to explain why this decision was made.',
        fields: [],
      },
    ],
  },
  [Sources.eti]: {
    id: Sources.eti,
    label: 'ETI',
    icon: 'ti-mail-question',
    paragraphs: [
      {
        text:
          'Our decision was based, in whole or in part, on information received ' +
          'from the recent questionnaire you completed in which you disclosed ' +
          'a history of {{applicableQuestionnaire}}. This information, based on ' +
          'our guidelines, disqualifies approval of your life insurance ' +
          'application.',
        juvenileText:
          'Our decision was based, in whole or in part, on information received ' +
          'from the recent questionnaire you completed in which you disclosed ' +
          'the proposed life insured’s history of {{applicableQuestionnaire}}. ' +
          'This information, based on our guidelines, disqualifies approval of ' +
          'your life insurance application.',
        fields: [
          {
            key: Fields.applicableQuestionnaire,
            placeholder: 'condition / history',
          },
        ],
      },
    ],
  },
  [Sources.labPiQture]: {
    id: Sources.labPiQture,
    label: 'LabPiQture',
    icon: 'ti-flask',
    paragraphs: [
      {
        text:
          'Our decision was based in part on information contained in prior ' +
          'lab results obtained from ExamOne.',
        juvenileText:
          'Our decision was based in part on information contained in prior ' +
          'lab results obtained from ExamOne and this information was ' +
          'requested in whole or in part because of information obtained in ' +
          'the investigation conducted as a result of the diagnostic history ' +
          'and/or prescription history report we received.',
        fields: [],
      },
      {
        text:
          'To obtain a copy of your report at no charge to you, please contact ' +
          'ExamOne **within 60 days** of the date of this letter. Any ' +
          'questions regarding the accuracy or completeness of the information ' +
          'in your report should be directed to ExamOne. Note that ExamOne did ' +
          'not make any decision in regard to your insurability and would not ' +
          'be able to explain why this decision was made. You may contact ' +
          'ExamOne by calling them at:',
        juvenileText:
          'To obtain a copy of the proposed life insured’s report at no charge ' +
          'to you, please contact ExamOne **within 60 days** of the date of ' +
          'this letter. Any questions regarding the accuracy or completeness ' +
          'of the information in the report should be directed to ExamOne. ' +
          'Note that ExamOne did not make any decision in regard to the ' +
          'proposed life insured’s insurability and would not be able to ' +
          'explain why this decision was made. You may contact ExamOne by ' +
          'calling them at:',
        fields: [],
        contactAfter:
          'ExamOne Consumer Disclosure\n' + '[1-844-255-8047](tel:18442558047)',
      },
    ],
  },
  [Sources.unresolvedMib]: {
    id: Sources.unresolvedMib,
    label: 'MIB',
    icon: 'ti-database',
    paragraphs: [
      {
        text: 'Our decision was based on information provided to us by MIB.',
        fields: [],
      },
      {
        text:
          'During the application process, we gave you a written description ' +
          'of the MIB, which is a consumer reporting agency that operates an ' +
          'information exchange on behalf of insurers. This authorization ' +
          'informed you that the MIB will arrange disclosure of any information ' +
          'it may have in your file. When we evaluated your application, the ' +
          'MIB sent a brief, coded report about you, to us, as you authorized ' +
          'in your application.',
        fields: [],
      },
      {
        text:
          'To obtain a copy of your MIB Consumer File at no charge to you, ' +
          'please contact MIB **within 60 days** of the date of this letter. ' +
          'You may contact MIB Information Office by mail, phone or online ' +
          'requests at:',
        fields: [],
        contactAfter:
          'MIB\n' +
          '50 Braintree Hill Park, Suite 400\n' +
          'Braintree, MA 02184-8734\n' +
          '[1-866-692-6901](tel:18666926901)\n' +
          '[www.mib.com](https://www.mib.com)',
      },
      {
        text:
          'Please be aware that the Medical Information Bureau (MIB) only ' +
          'provides consumer related data and/or information. This agency is ' +
          'not involved in, nor did they make any, underwriting decisions ' +
          'regarding your application.',
        fields: [],
      },
    ],
  },
  [Sources.mibAppEti]: {
    id: Sources.mibAppEti,
    label: 'MIB + ETI',
    icon: 'ti-database',
    paragraphs: [
      {
        text:
          'Our decision was based, in whole or in part, by information you ' +
          'provided on the {{applicableQuestionnaire}} questionnaire you ' +
          'completed.',
        fields: [
          {
            key: Fields.applicableQuestionnaire,
            placeholder: 'questionnaire name',
          },
        ],
      },
      {
        text:
          'In addition, our decision was based in part on information obtained ' +
          'in the consumer file received by the Medical Information Bureau ' +
          '(MIB). During the application process, we gave you a written ' +
          'description of the MIB, which is a consumer reporting agency that ' +
          'operates an information exchange on behalf of insurers. This ' +
          'authorization informed you that the MIB will arrange disclosure of ' +
          'any information it may have in your file. When we evaluated your ' +
          'application, the MIB sent a brief, coded report about you, to us, ' +
          'as you authorized in your application.',
        fields: [],
      },
      {
        text:
          'To obtain a copy of your MIB Consumer File at no charge to you, ' +
          'please contact MIB **within 60 days** of the date of this letter. ' +
          'You may contact MIB Information Office by mail, phone or online ' +
          'requests at:',
        fields: [],
        contactAfter:
          'MIB\n' +
          '50 Braintree Hill Park, Suite 400\n' +
          'Braintree, MA 02184-8734\n' +
          '[1-866-692-6901](tel:18666926901)\n' +
          '[www.mib.com](https://www.mib.com)',
      },
    ],
    juvenileParagraphs: [
      {
        text:
          'Your application for insurance was denied in whole or in part, by ' +
          'information received from the recent {{applicableQuestionnaire}} ' +
          'questionnaire you completed for the proposed life insured. This ' +
          'information was requested in whole or in part because of ' +
          'information obtained in the investigation conducted as a result of ' +
          'the MIB report we received.',
        fields: [
          {
            key: Fields.applicableQuestionnaire,
            placeholder: 'questionnaire name',
          },
        ],
      },
      {
        text:
          'During the application process, we gave you a written description ' +
          'of the MIB, a consumer reporting agency that operates an ' +
          'information exchange on behalf of insurers, together with a mailing ' +
          'address and telephone number of MIB and informed you that the MIB ' +
          'will arrange disclosure of any information it may have in your ' +
          'file. When we evaluated your application, the MIB sent a brief, ' +
          'coded report about the proposed life insured, to us, as you ' +
          'authorized in your application.',
        fields: [],
      },
      {
        text:
          'Also, you may obtain a copy of the proposed life insured’s MIB ' +
          'Consumer File at no charge if you contact MIB **within 60 days** ' +
          'of this date. You may contact the MIB Information Office at:',
        fields: [],
        contactAfter:
          'MIB\n' +
          '50 Braintree Hill Park, Suite 400\n' +
          'Braintree, MA 02184-8734\n' +
          '[1-866-692-6901](tel:18666926901)\n' +
          '[www.mib.com](https://www.mib.com)',
      },
      {
        text:
          'You have the right to know the specific reason why your application ' +
          'was declined. This may be requested by emailing Ethos **within 6 ' +
          'months** of the date of this letter at ' +
          '[accounts@ethoslife.com](mailto:accounts@ethoslife.com), one of our ' +
          'representatives will be glad to help.',
        fields: [],
      },
    ],
  },
  [Sources.mibAps]: {
    id: Sources.mibAps,
    label: 'MIB + APS',
    icon: 'ti-database',
    paragraphs: [
      {
        text:
          'Our decision was influenced, in whole or in part, by information ' +
          'contained in the medical records received from ' +
          '{{medicalFacilityOrProvider}}. To review your medical history, ' +
          'please contact the doctor’s office for your medical records.',
        fields: [
          {
            key: Fields.medicalFacilityOrProvider,
            placeholder: 'doctor / facility',
          },
        ],
      },
      {
        text:
          'In addition, our decision was based in part on information obtained ' +
          'in the consumer file received by the Medical Information Bureau ' +
          '(MIB). During the application process, we gave you a written ' +
          'description of the MIB, which is a consumer reporting agency that ' +
          'operates an information exchange on behalf of insurers. This ' +
          'authorization informed you that the MIB will arrange disclosure of ' +
          'any information it may have in your file. When we evaluated your ' +
          'application, the MIB sent a brief, coded report about you, to us, ' +
          'as you authorized in your application.',
        fields: [],
      },
      {
        text:
          'To obtain a copy of your MIB Consumer File at no charge to you, ' +
          'please contact MIB **within 60 days** of the date of this letter. ' +
          'You may contact MIB Information Office by mail, phone or online ' +
          'requests at:',
        fields: [],
        contactAfter:
          'MIB\n' +
          '50 Braintree Hill Park, Suite 400\n' +
          'Braintree, MA 02184-8734\n' +
          '[1-866-692-6901](tel:18666926901)\n' +
          '[www.mib.com](https://www.mib.com)',
      },
    ],
    juvenileParagraphs: [
      {
        text:
          'Your application for insurance was denied based on information ' +
          'documented in the medical records of the proposed life insured from ' +
          '{{medicalFacilityOrProvider}}. This information was requested in ' +
          'whole or in part because of information obtained in the ' +
          'investigation conducted as a result of the MIB report we received.',
        fields: [
          {
            key: Fields.medicalFacilityOrProvider,
            placeholder: 'doctor / facility',
          },
        ],
      },
      {
        text:
          'During the application process, we gave you a written description ' +
          'of the MIB, which is a consumer reporting agency that operates an ' +
          'information exchange on behalf of insurers. This authorization ' +
          'informed you that the MIB will arrange disclosure of any ' +
          'information it may have in the proposed life insured’s file. When ' +
          'we evaluated your application, the MIB sent a brief, coded report ' +
          'about the proposed life insured, to us, as you authorized in your ' +
          'application.',
        fields: [],
      },
      {
        text:
          'To obtain a copy of the proposed life insured’s MIB Consumer File ' +
          'at no charge to you, please contact MIB **within 60 days** of the ' +
          'date of this letter. You may contact MIB Information Office by ' +
          'mail, phone or online requests at:',
        fields: [],
        contactAfter:
          'MIB\n' +
          '50 Braintree Hill Park, Suite 400\n' +
          'Braintree, MA 02184-8734\n' +
          '[1-866-692-6901](tel:18666926901)\n' +
          '[www.mib.com](https://www.mib.com)',
      },
      {
        text:
          'You have the right to know the specific reason why your application ' +
          'was declined. This may be requested by emailing Ethos **within 6 ' +
          'months** of the date of this letter at ' +
          '[accounts@ethoslife.com](mailto:accounts@ethoslife.com), one of our ' +
          'representatives will be glad to help.',
        fields: [],
      },
    ],
  },
  [Sources.mibLabPiQture]: {
    id: Sources.mibLabPiQture,
    label: 'MIB + LabPiQture',
    icon: 'ti-database',
    paragraphs: [
      {
        text:
          'Our decision was based in part on information contained in prior ' +
          'lab results obtained from ExamOne.',
        fields: [],
      },
      {
        text:
          'To obtain a copy of your report at no charge to you, please contact ' +
          'ExamOne **within 60 days** of the date of this letter. Any ' +
          'questions regarding the accuracy or completeness of the information ' +
          'in your report should be directed to ExamOne. Note that ExamOne did ' +
          'not make any decision in regard to your insurability and would not ' +
          'be able to explain why this decision was made. You may contact ' +
          'ExamOne by calling them at:',
        fields: [],
        contactAfter:
          'ExamOne Consumer Disclosure\n' + '[1-844-255-8047](tel:18442558047)',
      },
      {
        text:
          'During the application process, we gave you a written description ' +
          'of the MIB, a consumer reporting agency that operates an ' +
          'information exchange on behalf of insurers, together with a mailing ' +
          'address and telephone number of MIB and informed you that the MIB ' +
          'will arrange disclosure of any information it may have in your ' +
          'file. When we evaluated your application, the MIB sent a brief, ' +
          'coded report about you, to us, as you authorized in your ' +
          'application.',
        fields: [],
      },
      {
        text:
          'You may obtain a copy of your MIB Consumer File at no charge if you ' +
          'contact MIB **within 60 days** of this date. You may contact the ' +
          'MIB Information Office at: MIB, 50 Braintree Hill Park, Suite 400, ' +
          'Braintree, MA 02184-8734 phone at ' +
          '[(866)-692-6901](tel:8666926901) or go to their website at ' +
          '[www.mib.com](https://www.mib.com) and request disclosure online.',
        fields: [],
      },
      {
        text:
          'You have the right to know the specific reason why your application ' +
          'was declined. This may be requested by emailing Ethos **within 6 ' +
          'months** of the date of this letter at ' +
          '[accounts@ethoslife.com](mailto:accounts@ethoslife.com), one of our ' +
          'representatives will be glad to help.',
        fields: [],
      },
      {
        text:
          'The attached notice describes your rights to access the information ' +
          'obtained for our files and explains the steps you may take if you ' +
          'believe the information is incorrect. This information supplements ' +
          'the material already given to you at the time of your application ' +
          'for coverage.',
        fields: [],
      },
    ],
    juvenileParagraphs: [
      {
        text:
          'Our decision was based in part on information contained in prior ' +
          'lab results obtained from ExamOne.',
        fields: [],
      },
      {
        text:
          'To obtain a copy of the proposed life insured’s report at no charge ' +
          'to you, please contact ExamOne **within 60 days** of the date of ' +
          'this letter. Any questions regarding the accuracy or completeness ' +
          'of the information in the proposed life insured’s report should be ' +
          'directed to ExamOne. Note that ExamOne did not make any decision in ' +
          'regard to the proposed life insured’s insurability and would not be ' +
          'able to explain why this decision was made. You may contact ExamOne ' +
          'by calling them at:',
        fields: [],
        contactAfter:
          'ExamOne Consumer Disclosure\n' + '[1-844-255-8047](tel:18442558047)',
      },
      {
        text:
          'During the application process, we gave you a written description ' +
          'of the MIB, which is a consumer reporting agency that operates an ' +
          'information exchange on behalf of insurers. This authorization ' +
          'informed you that the MIB will arrange disclosure of any ' +
          'information it may have in the proposed life insured’s file. When ' +
          'we evaluated your application, the MIB sent a brief, coded report ' +
          'about the proposed life insured, to us, as you authorized in your ' +
          'application.',
        fields: [],
      },
      {
        text:
          'To obtain a copy of the proposed life insured’s MIB Consumer File ' +
          'at no charge to you, please contact MIB **within 60 days** of the ' +
          'date of this letter. You may contact MIB Information Office by ' +
          'mail, phone or online requests at:',
        fields: [],
        contactAfter:
          'MIB\n' +
          '50 Braintree Hill Park, Suite 400\n' +
          'Braintree, MA 02184-8734\n' +
          '[1-866-692-6901](tel:18666926901)\n' +
          '[www.mib.com](https://www.mib.com)',
      },
      {
        text:
          'You have the right to know the specific reason why your application ' +
          'was declined. This may be requested by emailing Ethos **within 6 ' +
          'months** of the date of this letter at ' +
          '[accounts@ethoslife.com](mailto:accounts@ethoslife.com), one of our ' +
          'representatives will be glad to help.',
        fields: [],
      },
      {
        text:
          'The attached notice describes your rights to access the information ' +
          'obtained for our files and explains the steps you may take if you ' +
          'believe the information is incorrect. This information supplements ' +
          'the material already given to you at the time of your application ' +
          'for coverage.',
        fields: [],
      },
    ],
  },
  [Sources.mvr]: {
    id: Sources.mvr,
    label: 'MVR',
    icon: 'ti-car',
    paragraphs: [
      {
        text:
          'During the application process, you authorized the release of your ' +
          'motor vehicle history to help us determine your eligibility for ' +
          'insurance. Our decision was based, in whole or in part, on ' +
          'information we received regarding your Motor Vehicle Report. This ' +
          'information was obtained by TransUnion Driver’s Risk, you have the ' +
          'right to dispute any information contained in the report with ' +
          'TransUnion.',
        juvenileText:
          'Our underwriting decision was influenced, in whole or in part, by ' +
          'information received in the proposed life insured’s Motor Vehicle ' +
          'Report. This information was obtained by TransUnion Drivers Risk, ' +
          'you have the right to dispute any information contained in the ' +
          'report with TransUnion Drivers Risk. Please note that TransUnion ' +
          'Drivers Risk did not make this decision and they are not able to ' +
          'explain why the decision was made.',
        fields: [],
      },
      {
        text:
          'To obtain a copy of your motor vehicle record report at no charge ' +
          'to you, please contact the TransUnion Consumer Service Center ' +
          '**within 60 days** from the date of this letter. Note that ' +
          'TransUnion did not make this decision in regard to your ' +
          'insurability and would not be able to explain why the decision was ' +
          'made. They are located at:',
        juvenileText:
          'To obtain a copy of the proposed life insured’s motor vehicle ' +
          'record report at no charge to you, please contact the TransUnion ' +
          'Consumer Service Center **within 60 days** from the date of this ' +
          'letter. They are located at:',
        fields: [],
        contactAfter:
          'Datalink Consumer Relations\n' +
          '[supportCDR@transunion.com](mailto:supportCDR@transunion.com)\n' +
          '[866-454-3238](tel:8664543238)\n' +
          'P.O. Box 340639\n' +
          'Sacramento, CA 95834\n',
      },
    ],
  },
  [Sources.rxOrMedClaimsMilliman]: {
    id: Sources.rxOrMedClaimsMilliman,
    label: 'Rx or Med Claims (Milliman)',
    icon: 'ti-pill',
    paragraphs: [
      {
        text:
          'During the application process, you authorized the release of your ' +
          'prescription and diagnostic history to help us determine your ' +
          'eligibility for insurance. Our decision was based, in whole or in ' +
          'part, on information we received regarding your prescription drug ' +
          'and/or diagnostic history.',
        juvenileText:
          'Our decision was based in whole or in part on a report we received ' +
          'from Milliman IntelliScript regarding the proposed life insured’s ' +
          'prescription drug history or diagnostic history. During the ' +
          'application process, you authorized the release of the proposed ' +
          'life insured’s medical records to help us determine the proposed ' +
          'life insured’s eligibility for insurance. Please note that Milliman ' +
          'IntelliScript did not make this decision and they are not able to ' +
          'explain why the decision was made.',
        fields: [],
      },
      {
        text:
          'To obtain a copy of your prescription and/or diagnostic history ' +
          'report at no charge to you, please contact IntelliScript **within ' +
          '60 days** from the date of this letter. You have the right to ' +
          'dispute any information contained in the report with IntelliScript. ' +
          'Note that IntelliScript did not make this decision and they are not ' +
          'able to explain why the decision was made. You may contact ' +
          'IntelliScript by mail at:',
        juvenileText:
          'To obtain a copy of the proposed life insured’s prescription drug ' +
          'and/or diagnostic history at no charge to you, please contact ' +
          'Milliman IntelliScript **within 60 days** from the date of this ' +
          'letter. You have the right to dispute any information contained in ' +
          'the report with IntelliScript. You may contact Milliman ' +
          'IntelliScript by mail at:',
        fields: [],
        contactAfter:
          'Milliman IntelliScript\n' +
          'P.O. Box 2223, Brookfield, WI 53008\n' +
          '[1-(877) 211-4816](tel:18772114816) [Tollfree]',
        juvenileContactAfter:
          'Milliman IntelliScript\n' +
          'Website: [www.rxhistories.com/for-consumers](https://www.rxhistories.com/for-consumers)\n' +
          'Email: [FCRAReport@milliman.com](mailto:FCRAReport@milliman.com)\n' +
          'P.O. Box 2223, Brookfield, WI 53008\n' +
          'Toll-free: [(877) 211-4816](tel:18772114816)',
      },
    ],
  },
  [Sources.rxOrMedClaimsExamOne]: {
    id: Sources.rxOrMedClaimsExamOne,
    label: 'Rx or Med Claims (ExamOne)',
    icon: 'ti-pill',
    paragraphs: [
      {
        text:
          'During the application process, you authorized the release of your ' +
          'prescription and diagnostic history to help us determine your ' +
          'eligibility for insurance. Our decision was based, in whole or in ' +
          'part, on information we received regarding your prescription drug ' +
          'and/or diagnostic history.',
        juvenileText:
          'During the application process, you authorized the release of the ' +
          'proposed life insured’s prescription and diagnostic history to ' +
          'help us determine the proposed life insured’s eligibility for ' +
          'insurance. Our decision was based, in whole or in part, on ' +
          'information we received regarding the proposed life insured’s ' +
          'prescription drug history.',
        fields: [],
      },
      {
        text:
          'To obtain a copy of your prescription and/or diagnostic history ' +
          'report at no charge to you, please contact ExamOne **within 60 ' +
          'days** of the date of this letter. Any questions regarding the ' +
          'accuracy or completeness of the information in your report should ' +
          'be directed to ExamOne. Note that ExamOne did not make any ' +
          'decision in regard to your insurability and would not be able to ' +
          'explain why this decision was made. You may contact ExamOne by ' +
          'calling them at:',
        juvenileText:
          'To obtain a copy of the proposed life insured’s prescription and/or ' +
          'diagnostic history report at no charge to you, please contact ' +
          'ExamOne **within 60 days** of the date of this letter. Any ' +
          'questions regarding the accuracy or completeness of the information ' +
          'in the proposed life insured’s report should be directed to ' +
          'ExamOne. Note that ExamOne did not make any decision in regard to ' +
          'the proposed life insured’s insurability and would not be able to ' +
          'explain why this decision was made. You may contact ExamOne by ' +
          'calling them at:',
        fields: [],
        contactAfter:
          'ExamOne Consumer Disclosure\n' + '[1-844-255-8047](tel:18442558047)',
      },
    ],
  },
  [Sources.mibRxOrMedClaimsMilliman]: {
    id: Sources.mibRxOrMedClaimsMilliman,
    label: 'MIB + Rx or Med Claims (Milliman)',
    icon: 'ti-database',
    paragraphs: [
      {
        text:
          'During the application process, you authorized the release of your ' +
          'prescription and diagnostic history to help us determine your ' +
          'eligibility for insurance. Our decision was based, in whole or in ' +
          'part, on information we received regarding your prescription drug ' +
          'and/or diagnostic history.',
        fields: [],
      },
      {
        text:
          'To obtain a copy of your prescription and/or diagnostic history ' +
          'report at no charge to you, please contact IntelliScript **within ' +
          '60 days** from the date of this letter. You have the right to ' +
          'dispute any information contained in the report with IntelliScript. ' +
          'Note that IntelliScript did not make this decision and they are not ' +
          'able to explain why the decision was made. You may contact ' +
          'IntelliScript by mail at:',
        fields: [],
        contactAfter:
          'Milliman IntelliScript\n' +
          'P.O. Box 2223, Brookfield, WI 53008\n' +
          '[1-(877) 211-4816](tel:18772114816) [Tollfree]',
      },
      {
        text:
          'In addition, our decision was based in part on information obtained ' +
          'in the consumer file received by the Medical Information Bureau ' +
          '(MIB). During the application process, we gave you a written ' +
          'description of the MIB, which is a consumer reporting agency that ' +
          'operates an information exchange on behalf of insurers. This ' +
          'authorization informed you that the MIB will arrange disclosure of ' +
          'any information it may have in your file. When we evaluated your ' +
          'application, the MIB sent a brief, coded report about you, to us, ' +
          'as you authorized in your application.',
        fields: [],
      },
      {
        text:
          'To obtain a copy of your MIB Consumer File at no charge to you, ' +
          'please contact MIB **within 60 days** of the date of this letter. ' +
          'You may contact MIB Information Office by mail, phone or online ' +
          'requests at:',
        fields: [],
        contactAfter:
          'MIB\n' +
          '50 Braintree Hill Park, Suite 400\n' +
          'Braintree, MA 02184-8734\n' +
          '[1-866-692-6901](tel:18666926901)\n' +
          '[www.mib.com](https://www.mib.com)',
      },
    ],
    juvenileParagraphs: [
      {
        text:
          'Our decision was based in whole or in part on a report we received ' +
          'from Milliman IntelliScript regarding the proposed life insured\'s ' +
          'prescription drug history or diagnostic history. During the ' +
          'application process, you authorized the release of the proposed ' +
          'life insured\'s medical records to help us determine the proposed ' +
          'life insured\'s eligibility for insurance. Please note that Milliman ' +
          'IntelliScript did not make this decision and they are not able to ' +
          'explain why the decision was made.',
        fields: [],
      },
      {
        text:
          'To obtain a copy of the proposed life insured\'s prescription drug ' +
          'and/or diagnostic history at no charge to you, please contact ' +
          'Milliman IntelliScript **within 60 days** from the date of this ' +
          'letter. You have the right to dispute any information contained in ' +
          'the report with IntelliScript. You may contact Milliman ' +
          'IntelliScript by mail at:',
        fields: [],
        contactAfter:
          'Milliman IntelliScript\n' +
          'Website: [www.rxhistories.com/for-consumers](https://www.rxhistories.com/for-consumers)\n' +
          'Email: [FCRAReport@milliman.com](mailto:FCRAReport@milliman.com)\n' +
          'P.O. Box 2223, Brookfield, WI 53008\n' +
          'Toll-free: [(877) 211-4816](tel:18772114816)',
      },
      {
        text:
          'During the application process, we gave you a written description ' +
          'of the MIB, which is a consumer reporting agency that operates an ' +
          'information exchange on behalf of insurers. This authorization ' +
          'informed you that the MIB will arrange disclosure of any ' +
          'information it may have in the proposed life insured\'s file. When ' +
          'we evaluated your application, the MIB sent a brief, coded report ' +
          'about the proposed life insured, to us, as you authorized in your ' +
          'application.',
        fields: [],
      },
      {
        text:
          'To obtain a copy of the proposed life insured\'s MIB Consumer File ' +
          'at no charge to you, please contact MIB **within 60 days** of the ' +
          'date of this letter. You may contact MIB Information Office by ' +
          'mail, phone or online requests at:',
        fields: [],
        contactAfter:
          'MIB\n' +
          '50 Braintree Hill Park, Suite 400\n' +
          'Braintree, MA 02184-8734\n' +
          '[1-866-692-6901](tel:18666926901)\n' +
          '[www.mib.com](https://www.mib.com)',
      },
      {
        text:
          'You have the right to know the specific reason why your application ' +
          'was declined. This may be requested by emailing Ethos **within 6 ' +
          'months** of the date of this letter at ' +
          '[accounts@ethoslife.com](mailto:accounts@ethoslife.com), one of our ' +
          'representatives will be glad to help.',
        fields: [],
      },
    ],
  },
  [Sources.medClaimsAps]: {
    id: Sources.medClaimsAps,
    label: 'Med Claims + APS',
    icon: 'ti-stethoscope',
    paragraphs: [
      {
        text:
          'Our decision was influenced, in whole or in part, by information ' +
          'contained in the medical records received from ' +
          '{{medicalFacilityOrProvider}}. To review your medical history, ' +
          'please contact the doctor\'s office for your medical records. This ' +
          'information was requested in whole or in part because of information ' +
          'obtained in the investigation conducted as a result of the diagnostic ' +
          'history and/or prescription history report we received.',
        fields: [
          {
            key: Fields.medicalFacilityOrProvider,
            placeholder: 'doctor / facility',
          },
        ],
      },
      {
        text:
          'To obtain a copy of your prescription and/or diagnostic history ' +
          'report at no charge to you, please contact IntelliScript **within ' +
          '60 days** from the date of this letter. You have the right to ' +
          'dispute any information contained in the report with IntelliScript. ' +
          'Note that IntelliScript did not make this decision and they are not ' +
          'able to explain why the decision was made. You may contact ' +
          'IntelliScript by mail at:',
        fields: [],
        contactAfter:
          'Milliman IntelliScript\n' +
          'P.O. Box 2223, Brookfield, WI 53008\n' +
          '[1-(877) 211-4816](tel:18772114816) [Tollfree]',
      },
    ],
    juvenileParagraphs: [
      {
        text:
          'Your application for insurance was denied based on information ' +
          'documented in the proposed life insured\'s medical records from ' +
          '{{medicalFacilityOrProvider}}. This information was requested in ' +
          'whole or in part because of information obtained in the ' +
          'investigation conducted as a result of the proposed life insured\'s ' +
          'diagnostic history AND/OR prescription history report we received.',
        fields: [
          {
            key: Fields.medicalFacilityOrProvider,
            placeholder: 'doctor / facility',
          },
        ],
      },
      {
        text:
          'To obtain a copy of the proposed life insured\'s prescription drug ' +
          'and/or diagnostic history at no charge to you, please contact ' +
          'Milliman IntelliScript **within 60 days** from the date of this ' +
          'letter. You have the right to dispute any information contained in ' +
          'the report with IntelliScript. You may contact Milliman ' +
          'IntelliScript by mail at:',
        fields: [],
        contactAfter:
          'Milliman IntelliScript\n' +
          'Website: [www.rxhistories.com/for-consumers](https://www.rxhistories.com/for-consumers)\n' +
          'Email: [FCRAReport@milliman.com](mailto:FCRAReport@milliman.com)\n' +
          'P.O. Box 2223, Brookfield, WI 53008\n' +
          'Toll-free: [(877) 211-4816](tel:18772114816)',
      },
      {
        text:
          'You have the right to know the specific reason why your application ' +
          'was declined. This may be requested by emailing Ethos **within 6 ' +
          'months** of the date of this letter at ' +
          '[accounts@ethoslife.com](mailto:accounts@ethoslife.com), one of our ' +
          'representatives will be glad to help.',
        fields: [],
      },
    ],
  },
  [Sources.mibInsuranceLabs]: {
    id: Sources.mibInsuranceLabs,
    label: 'Insurance Labs',
    icon: 'ti-database',
    paragraphs: [
      {
        text:
          'Our decision was based on lab testing performed ' +
          '{{dateMeasurementTaken}} for the purpose of underwriting an ' +
          'insurance application for another carrier{{information}}. This ' +
          'information, based on our guidelines, disqualifies approval of your ' +
          'life insurance application.',
        fields: [
          { key: Fields.dateMeasurementTaken, placeholder: 'date' },
          {
            key: Fields.information,
            placeholder: 'carrier name (optional, include space and parentheses)',
          },
        ],
      },
      {
        text:
          'During the application process, we gave you a written description ' +
          'of the MIB, a consumer reporting agency that operates an ' +
          'information exchange on behalf of insurers, together with a mailing ' +
          'address and telephone number of MIB and informed you that the MIB ' +
          'will arrange disclosure of any information it may have in your ' +
          'file. When we evaluated your application, the MIB sent a brief, ' +
          'coded report about you, to us, as you authorized in your ' +
          'application.',
        fields: [],
      },
      {
        text:
          'You may obtain a copy of your MIB Consumer File at no charge if you ' +
          'contact MIB **within 60 days** of this date. You may contact the ' +
          'MIB Information Office at: MIB, 50 Braintree Hill Park, Suite 400, ' +
          'Braintree, MA 02184-8734 phone at ' +
          '[(866)-692-6901](tel:8666926901) or go to their website at ' +
          '[www.mib.com](https://www.mib.com) and request disclosure online.',
        fields: [],
      },
      {
        text:
          'You have the right to know the specific reason why your application ' +
          'was declined. This may be requested by emailing Ethos **within 6 ' +
          'months** of the date of this letter at ' +
          '[accounts@ethoslife.com](mailto:accounts@ethoslife.com), one of our ' +
          'representatives will be glad to help.',
        fields: [],
      },
      {
        text:
          'The attached notice describes your rights to access the information ' +
          'obtained for our files and explains the steps you may take if you ' +
          'believe the information is incorrect. This information supplements ' +
          'the material already given to you at the time of your application ' +
          'for coverage.',
        fields: [],
      },
    ],
    juvenileParagraphs: [
      {
        text:
          'Our decision was based on lab testing for the proposed life insured ' +
          'performed {{dateMeasurementTaken}} for the purpose of underwriting ' +
          'an insurance application for another carrier{{information}}. This ' +
          'information, based on our guidelines, disqualifies approval of your ' +
          'life insurance application.',
        fields: [
          { key: Fields.dateMeasurementTaken, placeholder: 'date' },
          {
            key: Fields.information,
            placeholder: 'carrier name (optional, include space and parentheses)',
          },
        ],
      },
      {
        text:
          'During the application process, we gave you a written description ' +
          'of the MIB, which is a consumer reporting agency that operates an ' +
          'information exchange on behalf of insurers. This authorization ' +
          'informed you that the MIB will arrange disclosure of any information ' +
          'it may have in the proposed life insured\'s file. When we evaluated ' +
          'your application, the MIB sent a brief, coded report about the ' +
          'proposed life insured, to us, as you authorized in your application.',
        fields: [],
      },
      {
        text:
          'To obtain a copy of the proposed life insured\'s MIB Consumer File ' +
          'at no charge to you, please contact MIB **within 60 days** of the ' +
          'date of this letter. You may contact MIB Information Office by ' +
          'mail, phone or online requests at:',
        fields: [],
        contactAfter:
          'MIB\n' +
          '50 Braintree Hill Park, Suite 400\n' +
          'Braintree, MA 02184-8734\n' +
          '[1-866-692-6901](tel:18666926901)\n' +
          '[www.mib.com](https://www.mib.com)',
      },
      {
        text:
          'You have the right to know the specific reason why your application ' +
          'was declined. This may be requested by emailing Ethos **within 6 ' +
          'months** of the date of this letter at ' +
          '[accounts@ethoslife.com](mailto:accounts@ethoslife.com), one of our ' +
          'representatives will be glad to help.',
        fields: [],
      },
      {
        text:
          'The attached notice describes your rights to access the information ' +
          'obtained for our files and explains the steps you may take if you ' +
          'believe the information is incorrect. This information supplements ' +
          'the material already given to you at the time of your application ' +
          'for coverage.',
        fields: [],
      },
    ],
  },
  [Sources.trl]: {
    id: Sources.trl,
    label: 'TRL',
    icon: 'ti-chart-bar',
    paragraphs: [
      {
        text:
          'Our decision was based, in whole or in part, on information ' +
          'contained in the consumer report received by TransUnion.',
        fields: [],
      },
      {
        text:
          'To obtain a copy of your report at no charge to you, please contact ' +
          'TransUnion **within 60 days** of the date of this letter. Any ' +
          'questions regarding the accuracy or completeness of the information ' +
          'in your report should be directed to TransUnion. Note that ' +
          'TransUnion did not make any decision in regard to your insurability ' +
          'and would not be able to explain why this decision was made. You ' +
          'may contact TransUnion by mail at:',
        juvenileText:
          'To obtain a copy of the proposed life insured’s report at no charge ' +
          'to you, please contact TransUnion **within 60 days** of the date ' +
          'of this letter. Any questions regarding the accuracy or ' +
          'completeness of the information in the proposed life insured’s ' +
          'report should be directed to TransUnion. Note that TransUnion did ' +
          'not make any decision in regard to the proposed life insured’s ' +
          'insurability and would not be able to explain why this decision was ' +
          'made. You may contact TransUnion by mail at:',
        fields: [],
        contactAfter:
          'TransUnion Consumer Disclosure\n' +
          'PO Box 2000\n' +
          'Chester, PA 19016-2000\n' +
          '[1-800-916-8800](tel:18009168800)\n' +
          '[www.transunion.com](https://www.transunion.com)',
      },
    ],
  },
  [Sources.custom]: {
    id: Sources.custom,
    label: 'Custom',
    icon: 'ti-pencil',
    paragraphs: [
      {
        text: '{{customContent}}',
        fields: [
          {
            key: Fields.customContent,
            placeholder: 'Enter custom paragraph text',
          },
        ],
      },
    ],
  },
};

// Text lifted from generator/containers/WithdrawalLetterContent.tsx (plus the
// Foreign Language reason). The opening and the "reapply" closing line live in
// letterFixedText.ts.
export const WITHDRAWAL_BLOCKS: LetterBlockSet = {
  [Sources.eti]: {
    id: Sources.eti,
    label: 'ETI',
    icon: 'ti-mail-question',
    paragraphs: [
      {
        text:
          'To date, we have not received all of the requirements necessary to ' +
          'review your application.\n' +
          'Specifically, we emailed you a short questionnaire to complete, ' +
          'which required your response in order for us to proceed with ' +
          'further consideration of your application. We have not received ' +
          'your response. Therefore, we are unable to continue review of your ' +
          'life insurance application, and your application has been ' +
          'withdrawn.',
        fields: [],
      },
    ],
    juvenileParagraphs: [
      {
        text:
          'To date, we have not received all of the requirements necessary to ' +
          'review your application. Specifically, we emailed you a short ' +
          'questionnaire to complete regarding the proposed life insured, ' +
          'which required your response in order for us to proceed with ' +
          'further consideration of your application. We have not received ' +
          'your response. Therefore, we are unable to continue review of your ' +
          'life insurance application and the application has been withdrawn.',
        fields: [],
      },
    ],
  },
  [Sources.foreignLanguage]: {
    id: Sources.foreignLanguage,
    label: 'Foreign Language',
    icon: 'ti-mail-question',
    paragraphs: [
      {
        text:
          'To date, we have not received all of the requirements necessary to ' +
          'review your application. Specifically, we requested additional ' +
          'information from you via an email questionnaire. Your responses ' +
          'were not in English. We cannot accept responses in a foreign ' +
          'language. Therefore, we are unable to continue review of your life ' +
          'insurance application, and your application has been withdrawn.',
        fields: [],
      },
    ],
  },
  [Sources.lackOfAps]: {
    id: Sources.lackOfAps,
    label: 'Lack of APS',
    icon: 'ti-stethoscope',
    paragraphs: [
      {
        text:
          'To date, we have not received all of the requirements necessary to ' +
          'review your application. In order to proceed with further ' +
          'consideration of your application, we requested a copy of your ' +
          'medical records from {{medicalFacilityOrProvider}}. We have not ' +
          'been able to secure records from your doctor. Therefore, we are ' +
          'unable to continue review of your life insurance application, and ' +
          'your application has been withdrawn.',
        fields: [
          {
            key: Fields.medicalFacilityOrProvider,
            placeholder: 'doctor / facility',
          },
        ],
      },
    ],
    juvenileParagraphs: [
      {
        text:
          'To date, we have not received all of the requirements necessary to ' +
          'review your application. In order to proceed with further ' +
          'consideration of your application, we requested a copy of the ' +
          'proposed life insured\u2019s medical records from ' +
          '{{medicalFacilityOrProvider}}. We have not been able to secure ' +
          'records from the doctor. Therefore, we are unable to continue ' +
          'review of your life insurance application and your application has ' +
          'been withdrawn.',
        fields: [
          {
            key: Fields.medicalFacilityOrProvider,
            placeholder: 'doctor / facility',
          },
        ],
      },
    ],
  },
  [Sources.lackOfInformation]: {
    id: Sources.lackOfInformation,
    label: 'Lack of Information',
    icon: 'ti-report-medical',
    paragraphs: [
      {
        text:
          'To date, we have not received all of the requirements necessary to ' +
          'review your application. Specifically, we requested additional ' +
          'information from you regarding {{information}}. We have not ' +
          'received adequate detail enough to continue underwriting. ' +
          'Therefore, we are unable to continue review of your life insurance ' +
          'application, and your application has been withdrawn.',
        fields: [{ key: Fields.information, placeholder: 'information' }],
      },
    ],
  },
  [Sources.miscellaneous]: {
    id: Sources.miscellaneous,
    label: 'Miscellaneous',
    icon: 'ti-file-description',
    paragraphs: [
      {
        text:
          'To date, we have not received all of the requirements necessary to ' +
          'review your application. Specifically, {{information}}. Therefore, ' +
          'we are unable to continue review of your life insurance ' +
          'application, and your application has been withdrawn.',
        fields: [{ key: Fields.information, placeholder: 'information' }],
      },
    ],
    juvenileParagraphs: [
      {
        text:
          'To date, we have not received all of the requirements necessary to ' +
          'review your application. Specifically, {{information}}. Therefore, ' +
          'we are unable to continue review of your life insurance ' +
          'application on the proposed life insured, and your application has ' +
          'been withdrawn.',
        fields: [{ key: Fields.information, placeholder: 'information' }],
      },
    ],
  },
  [Sources.pii]: {
    id: Sources.pii,
    label: 'PII',
    icon: 'ti-user',
    paragraphs: [
      {
        text:
          'We regret to inform you that we have formally withdrawn your ' +
          'application for life insurance coverage. The reason for the ' +
          'withdrawal is due to an error in your application. Specifically, ' +
          'the application does not accurately capture your correct ' +
          '{{piiDataType}}. Due to this discrepancy, we are unable to ' +
          'continue review of your life insurance application, and your ' +
          'application has been withdrawn.',
        fields: [{ key: Fields.piiDataType, placeholder: 'full legal name/date of birth/social security number' }],
      },
    ],
    juvenileParagraphs: [
      {
        text:
          'We regret to inform you that we have formally withdrawn your ' +
          'application for life insurance coverage. The reason for the ' +
          'withdrawal is due to an error in your application. Specifically, ' +
          'the application does not accurately capture the proposed life ' +
          'insured\u2019s correct {{piiDataType}}. Due to this discrepancy, we ' +
          'are unable to continue review of your life insurance application ' +
          'and your application has been withdrawn.',
        fields: [{ key: Fields.piiDataType, placeholder: 'full legal name/date of birth/social security number' }],
      },
    ],
  },
  [Sources.specialCharacter]: {
    id: Sources.specialCharacter,
    label: 'Special character',
    icon: 'ti-user',
    paragraphs: [
      {
        text:
          'We regret to inform you that we have formally withdrawn your ' +
          'application for life insurance coverage. The reason for the ' +
          'withdrawal is due to an unusual special character used in your ' +
          'name, on the application. If the character is not part of your ' +
          'actual full legal name, we cannot accept an application reflecting ' +
          'a special character in the name.',
        fields: [],
      },
    ],
  },
  [Sources.unresolvedMib]: {
    id: Sources.unresolvedMib,
    label: 'Unresolved MIB',
    icon: 'ti-database',
    paragraphs: [
      {
        text:
          'To date, we have not received all of the requirements necessary to ' +
          'review your application. Specifically, we received information ' +
          'from a third party source, and we have not been able to reconcile ' +
          'this information based on the details you provided in our ' +
          'questionnaire.',
        fields: [],
      },
      {
        text:
          'The information obtained was in a consumer file received from the ' +
          'Medical Information Bureau (MIB). During the application process, ' +
          'we gave you a written description of the MIB, which is a consumer ' +
          'reporting agency that operates an information exchange on behalf ' +
          'of insurers. This authorization informed you that the MIB will ' +
          'arrange disclosure of any information it may have in your file. ' +
          'When we evaluated your application, the MIB sent a brief, coded ' +
          'report about you, as you authorized in your application.',
        fields: [],
      },
      {
        text:
          'To obtain a copy of your MIB Consumer File at no charge to you, ' +
          'please contact MIB **within 60 days** of the date of this letter. ' +
          'You may contact MIB Information Office by mail, phone or online ' +
          'requests at:',
        fields: [],
        contactAfter:
          'MIB\n' +
          '50 Braintree Hill Park, Suite 400\n' +
          'Braintree, MA 02184-8734\n' +
          '[1-866-692-6901](tel:18666926901)\n' +
          '[www.mib.com](https://www.mib.com)',
      },
      {
        text:
          'Please be aware that the Medical Information Bureau (MIB) only ' +
          'provides consumer related data and/or information. This agency is ' +
          'not involved in, nor did they make any, underwriting decisions ' +
          'regarding your application.',
        fields: [],
      },
      {
        text:
          'The attached notice describes your rights to access the ' +
          'information obtained for our files and explains the steps you may ' +
          'take if you believe the information is incorrect. This ' +
          'information supplements the material already given to you at the ' +
          'time of your application for coverage.',
        fields: [],
      },
    ],
  },
  [Sources.custom]: {
    id: Sources.custom,
    label: 'Custom',
    icon: 'ti-pencil',
    paragraphs: [
      {
        text: '{{customContent}}',
        fields: [
          {
            key: Fields.customContent,
            placeholder: 'Enter custom paragraph text',
          },
        ],
      },
    ],
  },
};

export const BLOCKS_BY_LETTER_TYPE: Partial<
  Record<LetterTypes, LetterBlockSet>
> = {
  [LetterTypes.withdrawal]: WITHDRAWAL_BLOCKS,
  [LetterTypes.reinstatementDecline]: REINSTATEMENT_DECLINE_BLOCKS,
  [LetterTypes.otafSamePrice]: OTAF_SAME_BLOCKS,
  [LetterTypes.otaf]: OTAF_HIGHER_BLOCKS,
  [LetterTypes.decline]: DECLINE_BLOCKS,
};
